"use client";

import { useEffect } from "react";
import { X, Check, ExternalLink } from "lucide-react";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import type { Connection } from "./connectionTypes";

interface ConnectionDetailModalProps {
  connection: Connection | null;
  isConnected: boolean;
  onClose: () => void;
  onConnect: (connection: Connection) => void;
}

/**
 * Marketplace detail page for a connector — Notion-style About / Features /
 * How to use, fed by the catalog's `listing` (backend connector manifests).
 * Falls back to the card description when a connector has no listing yet
 * (older backend or static-fallback data).
 */
export default function ConnectionDetailModal({
  connection,
  isConnected,
  onClose,
  onConnect,
}: ConnectionDetailModalProps) {
  useEffect(() => {
    if (!connection) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [connection, onClose]);

  if (!connection) return null;

  const listing = connection.listing;

  return (
    // Backdrop click dismisses (target check keeps clicks inside the panel
    // from closing); keyboard users dismiss via the document-level Escape
    // handler above, so no key handler is needed here.
    <div
      role="presentation"
      className="fixed inset-0 z-[12000] bg-black/50 dark:bg-black/70 flex items-end md:items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: div+role matches the app's modal pattern; native <dialog> requires showModal() lifecycle management */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${connection.name} connection details`}
        className="w-full md:max-w-lg max-h-[85vh] flex flex-col rounded-t-xl md:rounded-xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-5 pb-4">
          <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-lg border border-zinc-200 dark:border-zinc-800 p-1.5">
            {connection.icon ?? (
              <BrandIcon id={connection.id} size={32} alt={connection.name} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {connection.name}
              </h2>
              {isConnected && (
                <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em] bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400">
                  <Check className="w-3 h-3" /> Connected
                </span>
              )}
            </div>
            {connection.badges && connection.badges.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {connection.badges.map((badge) => (
                  <span
                    key={badge}
                    className="px-1.5 py-0.5 rounded text-zinc-600 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 text-[9px] leading-none uppercase tracking-[0.04em] whitespace-nowrap"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-1 rounded-md text-zinc-500 hover-component dark:text-zinc-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto clean-scrollbar px-5 pb-2 space-y-5">
          <section>
            <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-zinc-500 dark:text-zinc-400 mb-1.5">
              About
            </h3>
            <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">
              {listing?.about || connection.description}
            </p>
          </section>

          {listing && listing.features.length > 0 && (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-zinc-500 dark:text-zinc-400 mb-1.5">
                Features
              </h3>
              <ul className="space-y-1.5">
                {listing.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex gap-2 text-sm leading-snug text-zinc-700 dark:text-zinc-300"
                  >
                    <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-green-600 dark:text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {listing && listing.howToUse.length > 0 && (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-[0.04em] text-zinc-500 dark:text-zinc-400 mb-1.5">
                How to use
              </h3>
              <ol className="space-y-1.5 list-none">
                {listing.howToUse.map((step, i) => (
                  <li
                    key={step}
                    className="flex gap-2 text-sm leading-snug text-zinc-700 dark:text-zinc-300"
                  >
                    <span className="w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500 tabular-nums">
                      {i + 1}.
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {listing?.helpUrl && (
            <a
              href={listing.helpUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline"
            >
              Learn more <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 pt-3 border-t border-zinc-100 dark:border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3 rounded-md text-sm font-medium text-zinc-700 dark:text-zinc-300 hover-component"
          >
            Close
          </button>
          {/* Embed connectors have no OAuth flow — the "How to use" steps
              above are the whole setup, so no Connect button. */}
          {!isConnected && connection.authKind !== "none" && (
            <button
              type="button"
              onClick={() => onConnect(connection)}
              className="h-8 px-3 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600"
            >
              Connect
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
